import { Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Divider } from '@/components/Layout/Divider';
import { DrawerProps } from '@/components/Layout/Drawer';

import { useDrawerOpenTracker } from '@/context';

import { useTranslate } from '@/hooks/useTranslate';

import {
  Document,
  DocumentSection,
  DocumentStatus,
  DocumentType,
  RequiredDocument,
  RequiredDocumentSection,
} from '@/types/document';

import { getDataTestId } from '@/utils/browser';

import { RequiredDocumentGroup } from '../RequiredDocuments/RequiredDocumentGroup';
import { TaxSupportingDocumentUploadGuidance } from '../utils/taxSupportingDocument';
import { RequiredDocumentUploadDrawer } from './RequiredDocumentUploadDrawer';
import { ViewDocumentDrawer } from './ViewDocumentDrawer';

import { StyledRequiredDocumentSelectDrawer } from './Drawers.styled';

type RequiredDocumentSelectDrawerProps = {
  section: RequiredDocumentSection;
  additionalSections?: RequiredDocumentSection[];
  clientId?: string | number;
  documentByDocType: Record<string, Record<string, Document>>;
  skipSelectWhenSingleDocument?: boolean;
  selectListHeaderKey?: string;
  uploadedListHeaderKey?: string;
  getUploadGuidance?: (
    docType?: DocumentType,
  ) => TaxSupportingDocumentUploadGuidance | undefined;
} & DrawerProps;

function getSelectableDocuments(sections: RequiredDocumentSection[]) {
  return sections.flatMap(drawerSection =>
    (drawerSection.groups || []).flatMap(group => group.items),
  );
}

export function RequiredDocumentSelectDrawer({
  section,
  additionalSections,
  clientId,
  onClose,
  documentByDocType,
  skipSelectWhenSingleDocument = false,
  selectListHeaderKey,
  uploadedListHeaderKey,
  getUploadGuidance,
  ...props
}: RequiredDocumentSelectDrawerProps) {
  const { t } = useTranslate();
  const [docTypeToUpload, setDocTypeToUpload] = useState<RequiredDocument>();
  const [docToView, setDocToView] = useState<Document>();
  const [docTypeToView, setDocTypeToView] = useState<RequiredDocument>();

  useDrawerOpenTracker(!!docToView || !!docTypeToUpload);

  const [activeSectionType, setActiveSectionType] = useState<string>(
    section.type as string,
  );
  const [showSelectList, setShowSelectList] = useState(true);
  const [useUploadedListHeader, setUseUploadedListHeader] = useState(false);
  const drawerSections = useMemo(
    () => (additionalSections?.length ? additionalSections : [section]),
    [additionalSections, section],
  );
  const activeSection = useMemo(
    () =>
      drawerSections.find(
        targetSection => String(targetSection.type) === activeSectionType,
      ) ?? section,
    [drawerSections, activeSectionType, section],
  );
  const selectableDocuments = useMemo(
    () => getSelectableDocuments(drawerSections),
    [drawerSections],
  );
  const isSingleDocumentSkip =
    skipSelectWhenSingleDocument && selectableDocuments.length === 1;
  const singleDocument = isSingleDocumentSkip
    ? selectableDocuments[0]
    : undefined;
  const hasUploadedSingleDocument =
    !!singleDocument?.status &&
    singleDocument.status !== DocumentStatus.Pending;
  const showUploadedListHeader =
    !!uploadedListHeaderKey &&
    isSingleDocumentSkip &&
    (useUploadedListHeader || hasUploadedSingleDocument);
  let listHeaderTitle: string | undefined;
  if (showUploadedListHeader && uploadedListHeaderKey) {
    listHeaderTitle = t(uploadedListHeaderKey);
  } else if (selectListHeaderKey) {
    listHeaderTitle = t(selectListHeaderKey);
  }

  const groupsHaveCountryTitles = drawerSections.some(drawerSection =>
    drawerSection.groups?.some(group => !!group.title),
  );
  const showSharedSelectHeader =
    !!selectListHeaderKey && !showUploadedListHeader && groupsHaveCountryTitles;
  const groupHeaderTitle = showSharedSelectHeader ? undefined : listHeaderTitle;

  const onSelectDocType = useCallback(
    (doc: RequiredDocument, targetSection: RequiredDocumentSection) => {
      const docKey = doc.groupedAs || doc.name;
      const targetSectionType = String(targetSection.type);
      setActiveSectionType(targetSectionType);
      if (
        doc.status !== DocumentStatus.Pending &&
        documentByDocType[targetSectionType]?.[docKey]
      ) {
        setDocToView(documentByDocType[targetSectionType][docKey]);
        setDocTypeToView(doc);
      } else {
        setDocTypeToUpload(doc);
      }
    },
    [documentByDocType],
  );

  const prevOpenRef = useRef(false);
  useEffect(() => {
    const isOpen = !!props.open;
    const isNewlyOpened = isOpen && !prevOpenRef.current;
    prevOpenRef.current = isOpen;
    if (!isNewlyOpened) return;

    const defaultSectionType = String(
      additionalSections?.[0]?.type ?? section.type,
    );
    setActiveSectionType(defaultSectionType);
    setDocTypeToUpload(undefined);
    setDocToView(undefined);
    setDocTypeToView(undefined);
    setUseUploadedListHeader(false);

    if (isSingleDocumentSkip && singleDocument) {
      const docKey = singleDocument.groupedAs || singleDocument.name;
      const hasUploadedDoc =
        singleDocument.status !== DocumentStatus.Pending &&
        !!documentByDocType[String(section.type)]?.[docKey];

      if (hasUploadedDoc) {
        setShowSelectList(true);
        setUseUploadedListHeader(true);
      } else {
        setShowSelectList(false);
        setDocTypeToUpload(singleDocument);
      }
      return;
    }

    setShowSelectList(true);
  }, [
    additionalSections,
    documentByDocType,
    isSingleDocumentSkip,
    props.open,
    section.type,
    singleDocument,
  ]);

  const hideUploader = () => {
    setDocTypeToUpload(undefined);
    if (isSingleDocumentSkip && !showSelectList) {
      onClose?.();
    }
  };

  const handleUploaded = () => {
    setDocTypeToUpload(undefined);
    if (isSingleDocumentSkip) {
      setUseUploadedListHeader(true);
      setShowSelectList(true);
    }
  };
  const hideViewer = () => {
    setDocToView(undefined);
    setDocTypeToView(undefined);
  };

  const isSelectDrawerOpen = !!props.open && showSelectList;

  return (
    <>
      <StyledRequiredDocumentSelectDrawer
        {...props}
        open={isSelectDrawerOpen}
        {...getDataTestId(`${section}-required-document-select-drawer`)}
        onClose={onClose}
        title={t(section.displayText || section.type)}
        bgColor={(theme: Theme) => theme.palette.neutral[100]}
        headerBgColor={(theme: Theme) => theme.palette.neutral[100]}
      >
        {showSharedSelectHeader && listHeaderTitle && (
          <Typography variant="h3" textAlign="center" mb={3}>
            {listHeaderTitle}
          </Typography>
        )}
        {drawerSections.map((drawerSection, sectionIndex) => {
          return (
            <Fragment key={`${drawerSection.type}-${sectionIndex}`}>
              {(drawerSection.groups || []).map((group, index) => (
                <Fragment
                  key={`required-document-group-${sectionIndex}-${index}`}
                >
                  <RequiredDocumentGroup
                    group={group}
                    headerTitle={groupHeaderTitle}
                    onItemSelect={doc => onSelectDocType(doc, drawerSection)}
                  />
                  {index <
                    (!drawerSection.groups
                      ? 0
                      : drawerSection.groups.length - 1) && (
                    <Divider
                      width="100%"
                      containerProps={{
                        my: 4,
                      }}
                    />
                  )}
                </Fragment>
              ))}
              {sectionIndex < drawerSections.length - 1 && (
                <Divider
                  width="100%"
                  containerProps={{
                    my: 4,
                  }}
                />
              )}
            </Fragment>
          );
        })}
      </StyledRequiredDocumentSelectDrawer>
      {/* #region Next step drawer */}
      <RequiredDocumentUploadDrawer
        open={!docToView && !!docTypeToUpload}
        section={activeSection.type as DocumentSection}
        type={docTypeToUpload?.groupedAs || docTypeToUpload?.name}
        clientId={clientId}
        onClose={hideUploader}
        onUploaded={handleUploaded}
        title={t(section.displayText || section.type)}
        uploadGuidance={getUploadGuidance?.(
          docTypeToUpload?.groupedAs || docTypeToUpload?.name,
        )}
        downloadPath={docTypeToUpload?.downloadPath}
        downloadFileName={docTypeToUpload?.downloadFileName}
      />
      <ViewDocumentDrawer
        open={!!docToView}
        section={activeSection}
        document={docToView}
        clientId={clientId}
        onClose={hideViewer}
        title={t(activeSection.displayText || activeSection.type)}
        downloadPath={docTypeToView?.downloadPath}
        downloadFileName={docTypeToView?.downloadFileName}
      />
      {/* #endregion */}
    </>
  );
}
